import { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import {
  fetchGallery,
  fetchReadme,
  fetchConfig,
  uploadPdf,
  createJob,
  getJob,
  retryJob,
  getVideoStreamUrl,
  resolveVideoUrl,
  type VideoEntry,
  type ModelOption,
  type JobDetail,
} from "./client";
import { MermaidDiagram } from "./components/MermaidDiagram";
import { HowItWorks } from "./components/HowItWorks";
import { PaperHero } from "./components/PaperHero";
import { JobMonitor } from "./components/studio/JobMonitor";

function useReadme() {
  const [readme, setReadme] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchReadme()
      .then((data) => {
        if (!cancelled) {
          setReadme(data);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load README");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => ({ readme, loading, error }), [readme, loading, error]);
}

const assetBase = (import.meta.env.BASE_URL ?? "/").replace(/\/$/, "");

function isAbsoluteUrl(path: string): boolean {
  return /^(?:[a-z]+:)?\/\//i.test(path);
}

function resolveAssetPath(path?: string): string | undefined {
  if (!path) return undefined;
  if (isAbsoluteUrl(path)) return path;
  const normalized = path.startsWith("/") ? path : `/${path}`;
  return `${assetBase}${normalized}`;
}

function getVideoSrc(video: VideoEntry): string {
  // Use resolveVideoUrl to handle cross-origin API URLs
  if (video.url) {
    return resolveVideoUrl(video.url);
  }
  // Fallback to the streaming endpoint to handle records that only store a local filesystem path.
  return getVideoStreamUrl(video.filename);
}

function getThumbnailSrc(video: VideoEntry): string | undefined {
  const explicit = resolveAssetPath(video.thumbnail);
  if (explicit) return explicit;
  if (!video.filename) return undefined;
  return resolveAssetPath(`/thumbnails/${video.filename}.jpg`);
}

async function handleDownload(url: string, filename: string): Promise<void> {
  try {
    // Fetch the video as a blob to handle cross-origin downloads
    const response = await fetch(url);
    if (!response.ok) throw new Error("Download failed");
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Create a temporary link and trigger download
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    // Fallback: open in new tab if fetch fails (e.g., CORS issues)
    window.open(url, "_blank");
  }
}

export default function App(): JSX.Element {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [thumbnailErrors, setThumbnailErrors] = useState<Record<string, boolean>>({});
  const [videos, setVideos] = useState<VideoEntry[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);
  const [videosError, setVideosError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const [topic, setTopic] = useState("");
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [modelKey, setModelKey] = useState<string | null>(null);
  const [useFeedback, setUseFeedback] = useState(true);
  const [funnyMode, setFunnyMode] = useState(false);
  const [theme, setTheme] = useState<"dark" | "light">("dark");
  const [orientation, setOrientation] = useState<"landscape" | "portrait">("landscape");
  const [layoutMode, setLayoutMode] = useState<
    "classic" | "centered" | "fullscreen" | "top_bottom" | "focus" | "cinematic" | "slide"
  >("classic");
  // Restore job ID from localStorage on mount
  const [jobId, setJobId] = useState<string | null>(() => {
    try {
      return localStorage.getItem("textbook2video_current_job_id");
    } catch {
      return null;
    }
  });
  const [jobDetail, setJobDetail] = useState<JobDetail | null>(null);
  const [jobError, setJobError] = useState<string | null>(null);

  // Persist job ID to localStorage when it changes
  useEffect(() => {
    try {
      if (jobId) {
        localStorage.setItem("textbook2video_current_job_id", jobId);
      } else {
        localStorage.removeItem("textbook2video_current_job_id");
      }
    } catch {
      // Ignore localStorage errors (e.g., private browsing)
    }
  }, [jobId]);
  const [submitting, setSubmitting] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const [models, setModels] = useState<ModelOption[]>([]);
  const [modelsError, setModelsError] = useState<string | null>(null);

  // Toast notification state
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" | "info" } | null>(null);

  // Auto-dismiss toast after 5 seconds
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(timer);
  }, [toast]);

  const { readme, loading: readmeLoading, error: readmeError } = useReadme();

  const promptSuggestions = [
    "Explain attention mechanism in transformers",
    "Introduction to quantum computing and qubits",
    "How does gradient descent work in machine learning?",
    "Explain the concept of blockchain and cryptocurrency",
    "What is DNA replication and transcription?",
    "Overview of the Krebs cycle in cellular respiration",
    "Introduction to calculus: derivatives and integrals",
    "Explain Newton's laws of motion with examples",
    "How do neural networks learn from data?",
    "Introduction to object-oriented programming concepts",
  ];

  const handleSuggestionClick = (suggestion: string) => {
    setTopic(suggestion);
    setShowSuggestions(false);
  };

  const loadVideos = () => {
    setVideosLoading(true);
    fetchGallery()
      .then((data) => {
        setVideos(data);
        setVideosError(null);
      })
      .catch((err: unknown) => {
        console.warn("Unable to load video gallery:", err);
        setVideosError(null); // Don't show error to users, show empty state instead
        setVideos([]); // Treat as empty gallery
      })
      .finally(() => setVideosLoading(false));
  };

  useEffect(() => {
    loadVideos();
    fetchConfig()
      .then((cfg) => {
        setModels(cfg.models ?? []);
        setModelsError(null);
      })
      .catch((err: unknown) => {
        console.warn("Unable to load API configuration, using defaults:", err);
        setModelsError(null); // Don't show error to users, fail gracefully
      });
  }, []);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    let prevStatus: string | undefined;
    const poll = async () => {
      try {
        const detail = await getJob(jobId);
        if (cancelled) return;
        setJobDetail(detail);

        // Show toast notification on status change to terminal state
        if (prevStatus !== detail.status) {
          if (detail.status === "completed") {
            setToast({ message: "Video generation completed successfully!", type: "success" });
          } else if (detail.status === "failed") {
            setToast({ message: "Job failed. Check logs for details.", type: "error" });
          } else if (detail.status === "cancelled") {
            setToast({ message: "Job was cancelled.", type: "info" });
          }
        }
        prevStatus = detail.status;

        if (detail.status === "completed" || detail.status === "failed" || detail.status === "cancelled") {
          loadVideos();
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (!cancelled) setJobError(err instanceof Error ? err.message : "Job polling failed");
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const handleThumbnailError = (filename: string) => {
    setThumbnailErrors((prev) => {
      if (prev[filename]) return prev;
      return { ...prev, [filename]: true };
    });
  };

  const filteredVideos = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter((v) => {
      const hay = `${v.display_name ?? ""} ${v.filename}`.toLowerCase();
      return hay.includes(q);
    });
  }, [videos, search]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setJobError(null);
    setJobDetail(null);
    setJobId(null);
    setSubmitting(true);
    try {
      let pdf_id: string | undefined;
      if (pdfFile) {
        const uploaded = await uploadPdf(pdfFile);
        pdf_id = uploaded.pdf_id;
      }
      if (!topic && !pdf_id) {
        throw new Error("Provide a topic or upload a PDF");
      }
      const job = await createJob({
        topic: topic || undefined,
        pdf_id,
        model_key: modelKey,
        code_model_key: modelKey,
        use_feedback: useFeedback,
        funny_mode: funnyMode,
        use_assets: true,
        save_pdf_index: true,
        theme,
        orientation,
        layout_mode: layoutMode,
      });
      setJobId(job.job_id);
    } catch (err) {
      setJobError(err instanceof Error ? err.message : "Failed to start job");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleRetry(mode: "restart" | "resume") {
    if (!jobId) return;
    setJobError(null);
    setSubmitting(true);
    try {
      const response = await retryJob(jobId, { mode });
      setJobId(response.job_id);
      setJobDetail(null);
    } catch (err) {
      setJobError(err instanceof Error ? err.message : "Failed to retry job");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 selection:bg-cyan-500/30 selection:text-cyan-200">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 sm:py-10">
        {/* Hero */}
        <header className="mb-10 sm:mb-16">
          <PaperHero />
          {/* Quick nav */}
          <div className="mt-6 flex justify-center gap-4">
            <a
              href="/studio"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-300 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
              Open Studio
            </a>
            <a
              href="#generate"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/20 transition-colors text-sm font-medium"
            >
              Generate Video
            </a>
            <a
              href="#gallery"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800/50 border border-slate-700 text-slate-300 hover:text-cyan-400 hover:border-cyan-500/50 transition-colors text-sm font-medium"
            >
              Gallery
            </a>
          </div>
        </header>

        {/* Generation Form */}
        <section className="mb-16 scroll-mt-24" id="generate">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-white/10 pb-2">
            <h2 className="text-2xl font-bold text-white">Generate a Video</h2>
            {submitting && (
              <span className="inline-flex items-center gap-2 text-sm text-cyan-400 animate-pulse">
                <span className="h-2 w-2 rounded-full bg-cyan-400"></span>
                Preparing job...
              </span>
            )}
          </div>
          
          <div className="glass-card relative overflow-hidden">
            <div className="absolute top-0 right-0 -mt-4 -mr-4 h-24 w-24 rounded-full bg-blue-500/20 blur-2xl"></div>
            
            <form className="grid gap-6 md:grid-cols-2 relative z-10" onSubmit={handleSubmit}>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className="label-dark mb-0">Topic</label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowSuggestions(!showSuggestions)}
                      className="text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                      </svg>
                      Suggestions
                    </button>
                    {showSuggestions && (
                      <>
                        <div
                          className="fixed inset-0 z-10"
                          onClick={() => setShowSuggestions(false)}
                        />
                        <div className="absolute right-0 mt-2 w-80 max-h-64 overflow-y-auto rounded-lg border border-cyan-500/30 bg-slate-800 shadow-xl z-20 animate-fade-in">
                          <div className="p-2 space-y-1">
                            {promptSuggestions.map((suggestion, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => handleSuggestionClick(suggestion)}
                                className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:text-white hover:bg-cyan-500/20 rounded transition-colors"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <textarea
                  className="input-dark"
                  rows={2}
                  placeholder="e.g., Explain attention mechanism in transformers"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  Or leave blank if you upload a PDF below.
                </p>
              </div>

              <div>
                <label className="label-dark">Upload PDF (optional)</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
                  className="block w-full text-sm text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-4 file:py-2 file:text-sm file:font-medium file:text-cyan-400 hover:file:bg-slate-700 transition-colors cursor-pointer"
                />
              </div>

              <div>
                <label className="label-dark">Code Model / Provider</label>
                <select
                  className="input-dark"
                  value={modelKey ?? ""}
                  onChange={(e) => setModelKey(e.target.value || null)}
                >
                  <option value="">Default (pipeline config)</option>
                  {models.map((m) => (
                    <option key={m.key} value={m.key}>
                      {m.display}
                    </option>
                  ))}
                </select>
                <p className="mt-1.5 text-xs text-slate-500">Controls the coding agent provider.</p>
              </div>

              <div>
                <label className="label-dark">Theme (background)</label>
                <select
                  className="input-dark"
                  value={theme}
                  onChange={(e) => setTheme((e.target.value as "dark" | "light") ?? "dark")}
                >
                  <option value="dark">Dark (black background, light foreground)</option>
                  <option value="light">Light (white background, dark foreground)</option>
                </select>
              </div>

              <div>
                <label className="label-dark">Aspect Ratio</label>
                <select
                  className="input-dark"
                  value={orientation}
                  onChange={(e) => setOrientation((e.target.value as "landscape" | "portrait") ?? "landscape")}
                >
                  <option value="landscape">Landscape (16:9)</option>
                  <option value="portrait">Portrait (9:16)</option>
                </select>
              </div>

              <div>
                <label className="label-dark">Layout Mode</label>
                <select
                  className="input-dark"
                  value={layoutMode}
                  onChange={(e) =>
                    setLayoutMode(
                      (e.target.value as
                        | "classic"
                        | "centered"
                        | "fullscreen"
                        | "top_bottom"
                        | "focus"
                        | "cinematic"
                        | "slide") ?? "classic",
                    )
                  }
                >
                  <option value="classic">Classic (left text / right grid)</option>
                  <option value="centered">Centered (graphics center, text bottom)</option>
                  <option value="fullscreen">Fullscreen (graphics full frame, overlay text)</option>
                  <option value="top_bottom">Top / Bottom (graphics top, text bottom)</option>
                  <option value="focus">Focus (minimal text, max graphics)</option>
                  <option value="cinematic">Cinematic (letterbox, lower-third text)</option>
                  <option value="slide">Slide (static slides with reveal animations)</option>
                </select>
              </div>

              <div className="flex flex-col justify-center gap-3">
                 <label className="inline-flex items-center gap-3 text-sm text-slate-300 cursor-pointer group">
                  <input
                    type="checkbox"
                    className="rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-900"
                    checked={useFeedback}
                    onChange={(e) => setUseFeedback(e.target.checked)}
                  />
                  <span className="group-hover:text-white transition-colors">Use automated bug-fixing</span>
                </label>
                <label className="inline-flex items-center gap-3 text-sm text-slate-300 cursor-pointer group">
                  <input
                    type="checkbox"
                    className="rounded border-slate-600 bg-slate-800 text-cyan-500 focus:ring-cyan-500 focus:ring-offset-slate-900"
                    checked={funnyMode}
                    onChange={(e) => setFunnyMode(e.target.checked)}
                  />
                  <span className="group-hover:text-white transition-colors">Funny mode</span>
                </label>
              </div>

              <div className="md:col-span-2 flex items-center gap-4 pt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-primary w-full sm:w-auto"
                >
                  {submitting ? "Submitting..." : "Start Job"}
                </button>
                {jobError && <span className="text-sm text-red-400 font-medium">{jobError}</span>}
              </div>
            </form>

            {/* Job status */}
            {jobId && (
              <div className="mt-8 rounded-xl border border-cyan-500/20 bg-cyan-900/10 p-5 animate-fade-in">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-3">
                  <div>
                    <p className="text-sm font-bold text-cyan-100">Job ID: {jobId}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="relative flex h-2 w-2">
                         <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75"></span>
                         <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500"></span>
                      </span>
                      <p className="text-xs text-cyan-200/80">
                        Status: <span className="font-mono uppercase text-cyan-300">{jobDetail?.status ?? "queued"}</span>
                        {jobDetail?.progress_stage ? ` · ${jobDetail.progress_stage}` : ""}
                        {jobDetail?.progress_message ? ` – ${jobDetail.progress_message}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                    {jobDetail?.video_url && (
                      <a
                        className="btn-primary text-xs py-1.5 px-3 w-full sm:w-auto text-center"
                        href={jobDetail.video_url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open Video
                      </a>
                    )}
                    {jobDetail?.status === "failed" && (
                      <>
                        <button
                          onClick={() => handleRetry("resume")}
                          disabled={submitting}
                          className="text-xs py-1.5 px-3 rounded-lg bg-orange-500/20 border border-orange-500/40 text-orange-200 hover:bg-orange-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Resume from Last Stage
                        </button>
                        <button
                          onClick={() => handleRetry("restart")}
                          disabled={submitting}
                          className="text-xs py-1.5 px-3 rounded-lg bg-red-500/20 border border-red-500/40 text-red-200 hover:bg-red-500/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Restart from Beginning
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {jobDetail?.logs?.length ? (
                  <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-black/20 bg-black/50 p-3 text-xs font-mono text-slate-300 scrollbar-thin">
                    {jobDetail.logs.map((line, idx) => (
                      <div key={idx} className="py-0.5 border-l-2 border-transparent hover:border-cyan-500/30 pl-2 hover:bg-white/5 transition-colors">
                        {line}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-slate-500 italic">Waiting for logs…</p>
                )}

                {/* Live job monitor with SSE-driven section progress */}
                <div className="mt-4 rounded-lg border border-cyan-500/20 bg-black/40">
                  <div className="px-3 pt-3 text-xs uppercase tracking-wide text-cyan-200/80 font-semibold">
                    Live Monitor
                  </div>
                  <div style={{ height: "360px" }} className="p-3">
                    <JobMonitor
                      jobId={jobId}
                      onComplete={() => loadVideos()}
                      onError={(err) => setJobError(err)}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Video Gallery Section */}
        <section className="mb-16 scroll-mt-24" id="gallery">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-2">
            <h2 className="text-2xl font-bold text-white">Video Gallery</h2>
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <input
                type="search"
                placeholder="Filter videos..."
                className="w-full sm:w-64 rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-sm text-white placeholder-slate-500 focus:border-cyan-500/50 focus:bg-black/40 focus:outline-none focus:ring-1 focus:ring-cyan-500/50 transition-all"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <button
                className="text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors whitespace-nowrap"
                onClick={loadVideos}
                disabled={videosLoading}
              >
                Refresh
              </button>
            </div>
          </div>

          {!videosLoading && filteredVideos.length === 0 && (
            <div className="glass-panel rounded-xl p-8 sm:p-12 text-center">
              <p className="text-slate-400">
                No videos available yet. Submit a job above to generate the first one.
              </p>
            </div>
          )}

          {!videosLoading && filteredVideos.length > 0 && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredVideos.map((video) => {
                const videoSrc = getVideoSrc(video);
                const thumbnailSrc = getThumbnailSrc(video);
                const shouldShowThumbnail = thumbnailSrc && !thumbnailErrors[video.filename];
                return (
                <div
                  key={video.filename}
                  className="group glass-card p-0 overflow-hidden hover:border-cyan-500/40 hover:shadow-cyan-500/10"
                >
                  <div className="aspect-video bg-black relative overflow-hidden">
                    {shouldShowThumbnail ? (
                      <img
                        src={thumbnailSrc}
                        alt={video.display_name ?? video.filename}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                        onClick={() => setSelectedVideo(videoSrc)}
                        onError={() => handleThumbnailError(video.filename)}
                      />
                    ) : (
                      <video
                        src={videoSrc}
                        className="h-full w-full object-cover"
                        preload="metadata"
                        onClick={() => setSelectedVideo(videoSrc)}
                      />
                    )}
                    
                    {/* Overlay */}
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    
                    <button
                      className="absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-300 group-hover:opacity-100 cursor-pointer"
                      onClick={() => setSelectedVideo(videoSrc)}
                    >
                         <div className="rounded-full bg-white/20 p-3 backdrop-blur-sm hover:scale-110 transition-transform">
                             <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                         </div>
                    </button>

                    <div className="pointer-events-none absolute bottom-3 left-3 right-3 flex items-center justify-between opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                      <span className="truncate rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white backdrop-blur-md border border-white/10">
                        {video.display_name ?? video.filename}
                      </span>
                      <span className="rounded-full bg-cyan-500/20 px-2 py-1 text-[10px] font-semibold text-cyan-200 backdrop-blur-md border border-cyan-500/20">
                        {(video.size_bytes / (1024 * 1024)).toFixed(1)} MB
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>{new Date(video.updated_at).toLocaleDateString()}</span>
                      <button
                        className="text-cyan-400 hover:text-cyan-300 font-medium transition-colors"
                        onClick={() => handleDownload(videoSrc, video.display_name ?? video.filename)}
                      >
                        Download &darr;
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </section>

        {/* How It Works (Mermaid) */}
        <section className="mb-16 scroll-mt-24" id="how-it-works">
          <div className="mb-6 flex items-center justify-between border-b border-white/10 pb-2">
            <h2 className="text-2xl font-bold text-white">How It Works</h2>
            <a
              className="text-sm font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
              href={`${import.meta.env.BASE_URL}diagram.png`}
              target="_blank"
              rel="noreferrer"
            >
              View diagram PNG
            </a>
          </div>
          {/* Assuming HowItWorks component can handle being in a dark container, 
              otherwise we might need to wrap it in a light container or update it. 
              For now, wrapping in a light-ish glass panel to ensure contrast for mermaid if it's dark text. 
              Actually, mermaid usually renders black text by default. 
              Let's wrap it in a lighter container to be safe, or a dark container if we can theme mermaid. 
              Safest bet for "facelift" without breaking diagrams: A clean white/slate panel. */}
          <div className="glass-card bg-white/95 p-6 overflow-x-auto">
             <HowItWorks />
          </div>
        </section>

        {/* Modal viewer */}
        {selectedVideo && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 animate-fade-in"
            onClick={() => setSelectedVideo(null)}
          >
            <div className="w-full max-w-6xl relative" onClick={(e) => e.stopPropagation()}>
              <div className="aspect-video rounded-xl overflow-hidden shadow-2xl shadow-black relative group">
                  <video src={selectedVideo} controls autoPlay className="w-full h-full bg-black" />
                  {/* Mobile/Touch friendly close button inside the video area (top-right) */}
                  <button
                    className="absolute top-4 right-4 rounded-full bg-black/50 p-2 text-white backdrop-blur-md hover:bg-black/70 transition-colors z-10"
                    onClick={() => setSelectedVideo(null)}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast notification */}
        {toast && (
          <div
            className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 rounded-xl px-5 py-4 shadow-2xl backdrop-blur-md animate-fade-in transition-all ${
              toast.type === "success"
                ? "bg-emerald-500/20 border border-emerald-500/40 text-emerald-100"
                : toast.type === "error"
                ? "bg-red-500/20 border border-red-500/40 text-red-100"
                : "bg-cyan-500/20 border border-cyan-500/40 text-cyan-100"
            }`}
          >
            {toast.type === "success" && (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {toast.type === "error" && (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            {toast.type === "info" && (
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span className="text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => setToast(null)}
              className="ml-2 rounded-full p-1 hover:bg-white/10 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* README Section */}
        <section className="mb-12 scroll-mt-24" id="docs">
          <h2 className="text-2xl font-bold text-white mb-6 border-b border-white/10 pb-2">Documentation</h2>
          <div className="glass-card bg-slate-900/80 p-8">
            {readmeLoading && (
              <div className="text-center text-slate-500">Loading documentation...</div>
            )}
            {readmeError && (
              <div className="rounded-lg bg-red-900/20 border border-red-500/20 p-4 text-red-400">
                {readmeError}
              </div>
            )}
            {!readmeLoading && !readmeError && readme && (
              <div className="prose prose-invert prose-blue max-w-none 
                prose-headings:text-slate-100 
                prose-a:text-cyan-400 prose-a:no-underline hover:prose-a:text-cyan-300 hover:prose-a:underline
                prose-code:text-cyan-200 prose-code:bg-slate-800/50 prose-code:px-1 prose-code:rounded
                prose-pre:bg-slate-950 prose-pre:border prose-pre:border-white/10">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    code(props: any) {
                      const { inline, className, children, ...rest } = props;
                      const match = /language-(\w+)/.exec(className ?? "");
                      const language = match?.[1]?.toLowerCase();
                      if (!inline && language === "mermaid") {
                        // Mermaid usually needs a light background or specific dark theme config
                        return (
                          <div className="bg-white rounded-lg p-4 my-4 overflow-x-auto">
                              <MermaidDiagram
                                definition={String(children).trim()}
                                config={{
                                  flowchart: {
                                    rankSpacing: 75,
                                    nodeSpacing: 50,
                                    padding: 10,
                                    useMaxWidth: true,
                                    htmlLabels: true,
                                    curve: "basis"
                                  },
                                  startOnLoad: false,
                                  theme: "default" // Keeping default for now to ensure visibility
                                }}
                              />
                          </div>
                        );
                      }
                      return (
                        <code className={className} {...rest}>
                          {children}
                        </code>
                      );
                    },
                    img(props) {
                      return <img loading="lazy" className="rounded-lg shadow-lg" {...props} />;
                    }
                  }}
                >
                  {readme}
                </ReactMarkdown>
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
